import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';
import NsFormGroup from '@britishgasenergy/react-nucleus/dist/nuc-components/NsFormGroup';
import { v4 as uuidv4 } from 'uuid';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import {
  setElectric,
  setGas,
  setGasElectric
} from 'state/fuel-type/fuel-type-slice';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { useNavigation } from 'hooks/useNavigation';
import { usePersonaliseQuoteScreen } from 'components/direct-sales/personalise-quote/hooks/usePersonaliseQuoteScreen';
import { setPaymentMethod } from 'state/payment-method/payment-method-slice';
import { stringToBool } from 'utils/misc';
import {
  selectElectricVehicle,
  setElectricVehicle
} from 'state/electric-vehicle/electric-vehicle-slice';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { useHasInitialState } from 'components/direct-sales/personalise-quote/hooks/useHasInitialState';
import { FUEL_OPTIONS } from 'config/app-config';
import { DirectDebitSavings } from 'components/direct-sales/personalise-quote/components/DirectDebitSavings';
import { NsCaveat } from 'components/common/ns-caveat/NsCaveat';

export const testId = 'personalizeQuoteTestId';

export const PersonaliseQuote = () => {
  const [key, setKey] = useState(uuidv4());
  const dispatch = useDispatch();
  const { eventTypes, next } = useNavigation();
  const {
    fuel: hasFuelInitialState,
    ev: hasEVInitialState,
    payment: hasPaymentInitialState
  } = useHasInitialState();

  const {
    paymentMethodValue,
    displayedPaymentOptions,
    hideFuelType,
    hideEV,
    hidePaymentMethod,
    shouldSkipPage,
    defaultPaymentMethod
  } = usePersonaliseQuoteScreen();

  const fuelType = useSelector(selectFuelType, shallowEqual);
  const fuelValue = getFuelValue(fuelType);
  const electricVehicle = useSelector(selectElectricVehicle, shallowEqual);

  const handleChangeFuelType = (event) => {
    const {
      target: { value }
    } = event;

    switch (value) {
      case FUEL_OPTIONS.ELECTRIC:
        dispatch(setElectric());
        break;
      case FUEL_OPTIONS.GAS:
        dispatch(setGas());
        dispatch(setElectricVehicle(false));
        break;
      default:
        dispatch(setGasElectric());
    }

    dispatch(setPaymentMethod(defaultPaymentMethod));
    setKey(uuidv4());
  };

  const handleChangePaymentMethod = (event) => {
    const {
      target: { value }
    } = event;

    dispatch(setPaymentMethod(value));
    dispatch(setElectricVehicle(false));
    setKey(uuidv4());
  };

  const handleChangeEV = (event) => {
    const {
      target: { value }
    } = event;
    const boolValue = stringToBool(value);

    dispatch(setElectricVehicle(boolValue));
  };

  const handleGoNext = () => {
    next({ eventType: eventTypes.NEXT });
  };

  useEffect(() => {
    hasPaymentInitialState &&
      !hasFuelInitialState &&
      dispatch(setPaymentMethod(defaultPaymentMethod));

    setKey(uuidv4()); // refresh the radio input. bug in Nucleus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelType]);

  useEffect(() => {
    hasFuelInitialState && dispatch(setGasElectric());
    hasEVInitialState && dispatch(setElectricVehicle(false));
    setKey(uuidv4()); // refresh the radio input. bug in Nucleus

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldSkipPage) {
      next({ eventType: eventTypes.NEXT, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkipPage]);

  if (shouldSkipPage) {
    return null;
  }

  return (
    <>
      <NsPanel testId={testId}>
        <NsBackCta />
        <NsContent>
          <NsTitle title="Personalise your quote" />
        </NsContent>
        <NsForm onSubmit={handleGoNext}>
          {!hideFuelType && (
            <NsFormGroup>
              <NsRadioInput
                key={`fuelType${key}`}
                heading="What type of quote would you like?"
                name="fuelType"
                onChange={handleChangeFuelType}
                value={fuelValue}
                className="inline"
                options={[
                  {
                    label: 'Gas & electricity',
                    value: FUEL_OPTIONS.DUAL
                  },
                  {
                    label: 'Electricity only',
                    value: FUEL_OPTIONS.ELECTRIC
                  },
                  {
                    label: 'Gas only',
                    value: FUEL_OPTIONS.GAS
                  }
                ]}
              />
            </NsFormGroup>
          )}
          {!hidePaymentMethod && (
            <NsFormGroup>
              <NsRadioInput
                key={`paymentMethod${key}`}
                heading="How would you like to pay?"
                name="paymentMethod"
                dataTestId="radioPaymentMethodTestId"
                onChange={handleChangePaymentMethod}
                value={paymentMethodValue}
                className="inline"
                options={displayedPaymentOptions}
              />
              <DirectDebitSavings paymentMethod={paymentMethodValue} />
            </NsFormGroup>
          )}

          {!hideEV && (
            <NsFormGroup>
              <NsRadioInput
                key={`electricVehicle${key}`}
                heading="Would you like an electric vehicle tariff?"
                name="electricVehicle"
                dataTestId="radioElectricVehicleTestId"
                onChange={handleChangeEV}
                value={electricVehicle}
                className="inline"
                options={[
                  {
                    label: 'No',
                    value: 'false'
                  },
                  {
                    label: 'Yes',
                    value: 'true'
                  }
                ]}
              />
            </NsFormGroup>
          )}

          <NsPrimaryCta>Continue</NsPrimaryCta>
        </NsForm>
      </NsPanel>

      <NsCaveat heading="Additional information" />
    </>
  );
};

export default PersonaliseQuote;
